import { api } from "@Aspecto/backend/convex/_generated/api";
import { useAuth, useClerk, useUser } from "@clerk/tanstack-react-start";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useRef, useState } from "react";

const INACTIVITY_LIMIT_MS = 14 * 24 * 60 * 60 * 1000;
const LAST_ACTIVITY_KEY = "afinio_last_activity";
const LAST_ACTIVITY_SESSION_KEY = "afinio_last_activity_session";

import Sidebar, { MobileHeader } from "@/components/sidebar";
import { ConsumerEmailBlockedCard, isConsumerEmail } from "@/components/auth/work-email-guard";
import { Button, buttonVariants } from "@/components/ui/button";
import { getGroupLabels } from "@/lib/group-labels";
import { cn } from "@/lib/utils";
import { invalidateClientClerkAuthCache } from "@/routes/__root";
import { useCurrentUser, useCurrentCompany } from "@/hooks/use-current-account";

// The primary entry point (app.afinio.co in prod, localhost in dev). Used to
// send a signed-in non-member back to where they can reach their own orgs.
function primaryAppOrigin(): string {
  if (typeof window === "undefined") return "https://app.afinio.co";
  const { protocol, hostname, port } = window.location;
  const isLocal = hostname === "localhost" || hostname.endsWith(".localhost");
  if (isLocal) return `${protocol}//localhost${port ? `:${port}` : ""}`;
  return "https://app.afinio.co";
}

function primaryAppHostLabel(): string {
  if (typeof window === "undefined") return "app.afinio.co";
  const { hostname } = window.location;
  return hostname.endsWith(".localhost") || hostname === "localhost"
    ? "the main app"
    : "app.afinio.co";
}

// Browser tab title: "<page> · <org> · Afinio" (e.g. "Women in Leadership · Acme Corporation · Afinio").
// On a space, the page segment is the space name; elsewhere it's the section name.
function getPageTitleSegment(
  pathname: string,
  groups: { _id: string; name: string }[] | undefined,
  labels: { singular: string; plural: string; singularLower: string },
): string | null {
  const path = pathname.replace(/\/+$/, "") || "/app";

  if (path === "/app") return "Overview";
  if (path === "/app/onboarding" || path === "/app/onboarding-group-admin") return null;
  if (path === "/app/groups") return labels.plural;
  if (path === "/app/groups/new") return `New ${labels.singularLower}`;

  const groupMatch = path.match(/^\/app\/groups\/([^/]+)/);
  if (groupMatch) {
    const group = groups?.find((g) => g._id === groupMatch[1]);
    return group?.name ?? labels.singular;
  }

  if (path.startsWith("/app/events")) return "Events";
  if (path === "/app/analytics") return "Analytics";
  if (path.startsWith("/app/notifications")) return "Notifications";
  if (path.startsWith("/app/settings")) return "Settings";
  if (path.startsWith("/app/admin")) return "Settings";
  if (path.startsWith("/app/profile/")) return "Profile";
  if (path.startsWith("/app/newsletters/")) return "Newsletter";

  return null;
}

const getSidebarState = createServerFn({ method: "GET" }).handler(async () => {
  const { getCookie } = await import("@tanstack/react-start/server");
  return getCookie("sidebar-collapsed") === "true";
});

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({ context, location }) => {
    if (!context.userId) {
      if ((location.search as { setup?: unknown }).setup === "complete") {
        throw redirect({ to: "/sign-in", search: { setup: "complete" } });
      }
      throw redirect({ to: "/sign-in" });
    }

    let sidebarCollapsed = false;
    if (typeof document !== "undefined") {
      sidebarCollapsed = document.cookie.includes("sidebar-collapsed=true");
    } else {
      sidebarCollapsed = await getSidebarState();
    }

    return { sidebarCollapsed };
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { isLoaded, isSignedIn, sessionId } = useAuth();
  const { isLoaded: isUserLoaded, user: clerkUser } = useUser();
  const { sidebarCollapsed, subdomain } = Route.useRouteContext();
  const { data: user } = useCurrentUser();
  const { data: company } = useCurrentCompany();
  const { data: groups } = useQuery(convexQuery(api.groups.getCompanyGroups, {}));
  const { data: subdomainCompany } = useQuery(
    convexQuery(api.companies.getBySubdomain, subdomain ? { subdomain } : "skip"),
  );
  const ensureLeadsSpace = useMutation(api.leadsSpace.ensureLeadsSpace);
  const initializeTimezone = useMutation(api.users.initializeTimezone);
  const { signOut } = useClerk();

  // Membership is invite-only. On a known org subdomain, a signed-in user with
  // no membership row for THIS org has no access — show a blocking page. (Since
  // getCurrentUser is now subdomain-scoped, `user === null` here means "not a
  // member of this org", even if the person belongs to other orgs.)
  const accessBlocked = isLoaded && isSignedIn && user === null && !!subdomainCompany;
  const consumerEmailBlocked =
    isLoaded &&
    isUserLoaded &&
    isSignedIn &&
    user === null &&
    isConsumerEmail(clerkUser?.primaryEmailAddress?.emailAddress);

  const [collapsed, setCollapsed] = useState(sidebarCollapsed);
  const timezoneInitializationInFlight = useRef<string | null>(null);

  // Track last activity and sign out after 14 days of inactivity.
  useEffect(() => {
    if (!sessionId) return;
    const activeSessionId = sessionId;

    function updateActivity() {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      localStorage.setItem(LAST_ACTIVITY_SESSION_KEY, activeSessionId);
    }
    function checkInactivity() {
      const storedSessionId = localStorage.getItem(LAST_ACTIVITY_SESSION_KEY);
      if (storedSessionId !== activeSessionId) {
        updateActivity();
        return;
      }

      const stored = localStorage.getItem(LAST_ACTIVITY_KEY);
      if (stored && Date.now() - Number(stored) > INACTIVITY_LIMIT_MS) {
        invalidateClientClerkAuthCache();
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        localStorage.removeItem(LAST_ACTIVITY_SESSION_KEY);
        void signOut({ redirectUrl: "/sign-in" });
      } else {
        updateActivity();
      }
    }

    checkInactivity();

    document.addEventListener("click", updateActivity, { passive: true });
    document.addEventListener("keydown", updateActivity, { passive: true });

    function handleVisibility() {
      if (document.visibilityState === "visible") checkInactivity();
    }
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      document.removeEventListener("click", updateActivity);
      document.removeEventListener("keydown", updateActivity);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [sessionId, signOut]);

  // When the session becomes invalid (e.g. it expired while the tab was idle),
  // Clerk flips to signed-out. Redirect to sign-in right away instead of leaving
  // the user staring at an empty, perpetually-loading app shell.
  const sessionExpired = isLoaded && !isSignedIn;
  useEffect(() => {
    if (sessionExpired) {
      navigate({ to: "/sign-in", replace: true });
    }
  }, [sessionExpired, navigate]);

  const toggleSidebar = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      document.cookie = `sidebar-collapsed=${next};path=/;max-age=31536000;SameSite=Lax`;
      return next;
    });
  }, []);

  const isOnboardingPage =
    location.pathname === "/app/onboarding" || location.pathname === "/app/onboarding-group-admin";

  const groupLabels = getGroupLabels(company?.groupLabel);
  useEffect(() => {
    const pageSegment = getPageTitleSegment(location.pathname, groups, groupLabels);
    document.title = [pageSegment, company?.name, "Afinio"].filter(Boolean).join(" · ");
  }, [
    location.pathname,
    groups,
    company?.name,
    groupLabels.singular,
    groupLabels.plural,
    groupLabels.singularLower,
  ]);

  useEffect(() => {
    if (!company || !subdomain || isOnboardingPage) return;
    if (company.subdomain && company.subdomain !== subdomain) {
      window.location.href = `https://${company.subdomain}.afinio.co/app`;
    }
  }, [company, subdomain, isOnboardingPage]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !user || user.timezone) {
      return;
    }

    if (timezoneInitializationInFlight.current === user._id) {
      return;
    }

    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!browserTimezone) {
      return;
    }

    timezoneInitializationInFlight.current = user._id;
    void initializeTimezone({ timezone: browserTimezone })
      .then(() => router.invalidate())
      .catch((error) => {
        timezoneInitializationInFlight.current = null;
        console.error("Failed to initialize timezone", error);
      });
  }, [initializeTimezone, isLoaded, isSignedIn, router, user]);

  useEffect(() => {
    if (user?.onboardingCompleted) {
      void ensureLeadsSpace().then(() =>
        queryClient.invalidateQueries({
          queryKey: convexQuery(api.groups.getCompanyGroups, {}).queryKey,
        }),
      );
    }
  }, [user?._id, user?.onboardingCompleted, ensureLeadsSpace, queryClient]);

  useEffect(() => {
    if (user && !user.onboardingCompleted && !isOnboardingPage) {
      if (user.role === "rlg_admin") {
        navigate({ to: "/app/onboarding-group-admin" });
      } else {
        navigate({ to: "/app/onboarding" });
      }
    }
    if (user?.onboardingCompleted && isOnboardingPage) {
      navigate({ to: "/app" });
    }
  }, [user, isOnboardingPage, navigate]);

  // app.afinio.co is an entry point, not an organization: it must not serve org
  // content. A signed-in, onboarded user who lands on the app origin is sent to
  // /select-org, which redirects to their org's subdomain (or shows a picker if
  // they belong to several). Onboarding (a new org has no subdomain yet) stays
  // on the app origin.
  useEffect(() => {
    if (subdomain || isOnboardingPage) return;
    if (user?.onboardingCompleted && company?.subdomain) {
      navigate({ to: "/select-org" });
    }
  }, [subdomain, isOnboardingPage, user?.onboardingCompleted, company?.subdomain, navigate]);

  // Session expired / signed out — show a spinner while the redirect above runs,
  // never the empty app shell with stuck skeletons.
  if (sessionExpired) {
    return (
      <div className="flex h-svh items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
      </div>
    );
  }

  if (consumerEmailBlocked) {
    return (
      <ConsumerEmailBlockedCard
        email={clerkUser?.primaryEmailAddress?.emailAddress}
        onSignOut={() => void signOut({ redirectUrl: "/create" })}
      />
    );
  }

  if (accessBlocked) {
    return (
      <div className="flex flex-1 items-center justify-center px-4">
        <div className="w-full max-w-100 rounded-2xl border bg-card px-8 py-10 text-center shadow-sm">
          <h1 className="text-xl font-semibold tracking-tight">No access to this workspace</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            You are not a member of this organization. Ask an administrator for an invitation, or go
            to the main app to access your organizations.
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <a href={primaryAppOrigin()} className={cn(buttonVariants())}>
              Go to {primaryAppHostLabel()}
            </a>
            <Button variant="outline" onClick={() => void signOut({ redirectUrl: "/sign-in" })}>
              Sign out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isOnboardingPage) {
    return (
      <div className="flex min-h-svh w-full min-w-0 flex-1 flex-col bg-background lg:min-h-0">
        <Outlet />
      </div>
    );
  }

  return (
    <div className="flex h-svh flex-col lg:flex-row">
      <MobileHeader />
      <Sidebar collapsed={collapsed} onToggle={toggleSidebar} />
      <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto bg-background">
        <Outlet />
      </main>
    </div>
  );
}
